import { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Trophy, Loader2, Check, Vote, Undo2, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { BADGE_LABELS } from "../pages/Awards";

// Award badge chips for detail pages / cards. entity.award_badges = [{badge_type, category_name, season_name}]
export function AwardBadges({ badges = [], compact = false }) {
  if (!badges.length) return null;
  const tone = (t) => (["winner", "artist_of_year", "venue_of_year", "category_winner"].includes(t)
    ? "bg-amber-400 text-black" : t === "finalist" ? "bg-amber-400/25 text-amber-200" : "bg-sky-500/25 text-sky-200");
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="award-badges">
      {badges.slice(0, compact ? 1 : 6).map((b, i) => (
        <span key={i} className={`text-[10px] font-extrabold px-2 py-0.5 rounded-full flex items-center gap-1 ${tone(b.badge_type)}`}>
          <Trophy size={9} /> {BADGE_LABELS[b.badge_type] || b.badge_type}{!compact && b.category_name ? ` · ${b.category_name}` : ""}
        </span>
      ))}
    </div>
  );
}

// Live voting card shown on nominated artist/venue profiles. Same vote logic as awards pages.
export function VoteCard({ entityType, entity, compact = false }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const load = useCallback(() => api.get(`/awards/profile-card?entity_type=${entityType}&entity_id=${entity.id}`)
    .then((r) => setData(r.data)).catch(() => setData({ show: false })), [entityType, entity.id]);
  useEffect(() => { load(); }, [load]);

  if (!data || !data.show) return null;
  const label = entityType === "artist" ? "artist" : "venue";

  const vote = async (season, cat) => {
    if (!user) { toast.info("Log in to vote — you'll come right back here"); nav("/login", { state: { from: loc.pathname } }); return; }
    setBusy(cat.category_id);
    try {
      const { data: r } = await api.post("/awards/vote", { season_id: season.season_id, category_id: cat.category_id, entity_type: entityType, entity_id: entity.id });
      toast.success(r.message); load();
    } catch (e) { toast.error(errMsg(e)); load(); }
    setBusy("");
  };
  const unvote = async (cat) => {
    setBusy(cat.category_id);
    try {
      const { data: r } = await api.post("/awards/unvote", { category_id: cat.category_id, entity_id: entity.id });
      toast.success(r.message); load();
    } catch (e) { toast.error(errMsg(e)); load(); }
    setBusy("");
  };

  return (
    <div className={compact ? "" : "mx-6 md:mx-12 lg:mx-20 mt-6 mb-2 relative z-10"} data-testid={`vote-card-${entityType}`}>
      <div className={`bg-gradient-to-r from-amber-400/10 to-rose-500/10 border border-amber-400/30 rounded-2xl ${compact ? "p-4" : "p-5 md:p-6"}`}>
        {data.seasons.map((s) => (
          <div key={s.season_id} className="mb-4 last:mb-0">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <p className="font-display font-bold text-lg flex items-center gap-2">
                <Trophy size={18} className="text-amber-400" /> The VIP Spotlight Awards <span className="text-zinc-400 text-sm font-sans">· {s.season_name}</span>
              </p>
              <p className="text-xs font-bold" data-testid="vote-card-status">
                {s.flags.results_published ? <span className="text-amber-300">Results published</span>
                  : s.flags.voting_open ? <span className="text-emerald-300">Voting open{s.voting_end ? ` — closes ${s.voting_end}` : ""}</span>
                  : <span className="text-sky-300">Nominated{s.voting_start ? ` — voting opens ${s.voting_start}` : " — voting opens soon"}</span>}
              </p>
            </div>
            <p className="text-sm text-zinc-300 mb-3">{s.flags.voting_open ? `Vote for this ${label} in The VIP Spotlight Awards!` : `This ${label} is nominated for The VIP Spotlight Awards.`}</p>
            <div className="space-y-2">
              {s.categories.map((c) => (
                <div key={c.category_id} className="flex flex-wrap items-center justify-between gap-2 bg-black/30 rounded-xl px-4 py-2.5" data-testid={`vote-row-${c.category_slug}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <p className="font-medium text-sm truncate">{c.category_name}</p>
                    {c.winner && <span className="bg-amber-400 text-black text-[10px] font-extrabold px-2 py-0.5 rounded-full shrink-0">🏆 Winner</span>}
                    {"vote_count" in c && <span className="text-xs text-zinc-500 shrink-0" data-testid={`vote-row-count-${c.category_slug}`}>{c.vote_count} vote{c.vote_count === 1 ? "" : "s"}</span>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Link to={`/awards/${s.season_slug}/${c.category_slug}`} className="text-xs text-zinc-400 hover:text-white flex items-center gap-0.5" data-testid={`vote-row-link-${c.category_slug}`}>Category <ChevronRight size={11} /></Link>
                    {s.flags.voting_open && (c.my_vote ? (
                      data.settings.allow_vote_undo
                        ? <button onClick={() => unvote(c)} disabled={busy === c.category_id} className="text-xs bg-emerald-500/20 text-emerald-300 px-3 py-1.5 rounded-full flex items-center gap-1 font-bold" data-testid={`vote-card-unvote-${c.category_slug}`}>{busy === c.category_id ? <Loader2 size={11} className="animate-spin" /> : <Undo2 size={11} />} Voted — undo</button>
                        : <span className="text-xs text-emerald-300 flex items-center gap-1 font-bold"><Check size={12} /> Voted</span>
                    ) : (
                      <button onClick={() => vote(s, c)} disabled={busy === c.category_id} className="text-xs bg-rose-500 hover:bg-rose-600 font-bold px-4 py-1.5 rounded-full flex items-center gap-1" data-testid={`vote-card-vote-${c.category_slug}`}>{busy === c.category_id ? <Loader2 size={11} className="animate-spin" /> : <Vote size={11} />} Vote</button>
                    ))}
                    {!s.flags.voting_open && !s.flags.results_published && <span className="text-xs text-zinc-500">Voting not open yet</span>}
                    {s.flags.results_published && !s.flags.voting_open && !c.winner && <span className="text-xs text-zinc-500">Voting closed</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function NominateButton({ entityType, entity }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    if (open) api.get(`/awards/eligible?entity_type=${entityType}&entity_id=${entity.id}`).then((r) => setData(r.data)).catch(() => setData([]));
  }, [open, entityType, entity.id]);

  const nominate = async (season, cat) => {
    if (!user) { toast.info("Log in to nominate"); nav("/login"); return; }
    setBusy(cat.id);
    try {
      const { data: r } = await api.post("/awards/nominate", { season_id: season.id, category_id: cat.id, entity_type: entityType, entity_id: entity.id });
      toast.success(r.message);
      setData((d) => d.map((g) => g.season.id === season.id ? { ...g, categories: g.categories.map((c) => c.id === cat.id ? { ...c, already_nominated_by_me: true } : c) } : g));
    } catch (e) { toast.error(errMsg(e)); }
    setBusy("");
  };

  return (
    <>
      <button onClick={() => setOpen(true)} className="flex items-center gap-2 border border-amber-400/40 text-amber-300 hover:bg-amber-400/10 font-bold px-5 py-2.5 rounded-full text-sm transition-colors" data-testid={`nominate-btn-${entityType}`}>
        <Trophy size={15} /> Nominate for VIP Spotlight Awards
      </button>
      {open && (
        <Dialog open onOpenChange={(o) => !o && setOpen(false)}>
          <DialogContent className="bg-[#111] border-white/10 text-white max-w-md max-h-[80vh] overflow-y-auto" data-testid="nominate-dialog">
            <DialogHeader>
              <DialogTitle className="font-display flex items-center gap-2"><Trophy size={18} className="text-amber-400" /> Nominate {entity.name}</DialogTitle>
              <DialogDescription className="text-zinc-400 text-sm">Pick a category. One nomination per category per season.</DialogDescription>
            </DialogHeader>
            {data === null ? <p className="text-sm text-zinc-500">Loading…</p> : data.length === 0 ? (
              <p className="text-sm text-zinc-500" data-testid="nominate-none">No award seasons are accepting nominations right now — check back soon!</p>
            ) : data.map((g) => (
              <div key={g.season.id}>
                <p className="text-xs font-bold uppercase tracking-wide text-zinc-500 mb-2">{g.season.name}</p>
                <div className="space-y-2">
                  {g.categories.map((c) => (
                    <button key={c.id} disabled={c.already_nominated_by_me || busy === c.id} onClick={() => nominate(g.season, c)}
                      className={`w-full text-left border rounded-xl p-3 flex items-center justify-between gap-2 ${c.already_nominated_by_me ? "border-emerald-500/30 bg-emerald-500/5 cursor-default" : "border-white/10 hover:border-amber-400/40"}`}
                      data-testid={`nominate-cat-${c.slug}`}>
                      <div><p className="font-medium text-sm">{c.name}</p>{c.description && <p className="text-xs text-zinc-500">{c.description}</p>}</div>
                      {c.already_nominated_by_me ? <span className="text-xs text-emerald-300 flex items-center gap-1 shrink-0"><Check size={12} /> Nominated</span>
                        : busy === c.id ? <Loader2 size={14} className="animate-spin shrink-0" /> : <span className="text-xs text-amber-300 shrink-0">Nominate →</span>}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
